import { useState, useEffect, useMemo } from 'react';
import { Download, Eye, Check, Trash2, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { API, authFetch, formatDate, exportCSV } from './helpers';

const ContactsTab = ({ user }) => {
  const [contacts, setContacts] = useState([]);
  const [expandedContact, setExpandedContact] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchContacts = async () => {
    const res = await authFetch(`${API}/contacts`);
    if (res.ok) setContacts(await res.json());
  };

  useEffect(() => { fetchContacts(); }, []);

  const filtered = useMemo(() => {
    let items = contacts;
    if (statusFilter !== 'all') items = items.filter(c => c.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(c =>
        c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.message?.toLowerCase().includes(q)
      );
    }
    return items;
  }, [contacts, search, statusFilter]);

  const updateStatus = async (id, status) => {
    await authFetch(`${API}/contacts/${id}/status?status=${status}`, { method: 'PATCH' });
    fetchContacts();
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    await authFetch(`${API}/contacts/${id}`, { method: 'DELETE' });
    fetchContacts();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-brand-dark">Contact Submissions</h2>
        <button onClick={() => exportCSV('contacts')} className="flex items-center gap-2 bg-white border border-gray-300 px-4 py-2 text-sm font-medium text-brand-dark hover:bg-gray-50" data-testid="export-contacts-btn">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="contacts-filters">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, or message..." className="pl-10" data-testid="contacts-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-gray-300 px-3 py-2 rounded text-sm" data-testid="contacts-status-filter">
          <option value="all">All Status</option>
          <option value="new">New</option>
          <option value="read">Read</option>
          <option value="responded">Responded</option>
        </select>
      </div>

      <p className="text-xs text-gray-400 mb-4">{filtered.length} of {contacts.length} contacts</p>

      {filtered.length === 0 ? (
        <p className="text-gray-500">{contacts.length === 0 ? 'No contact submissions yet.' : 'No contacts match your search.'}</p>
      ) : (
        <div className="space-y-4" data-testid="contacts-list">
          {filtered.map((contact) => (
            <div key={contact.id} className="bg-white p-4" data-testid={`contact-${contact.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-medium text-brand-dark">{contact.name}</h3>
                    <span className={`px-2 py-0.5 text-xs font-medium ${contact.status === 'new' ? 'bg-yellow-100 text-yellow-800' : contact.status === 'read' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>{contact.status}</span>
                  </div>
                  <p className="text-sm text-gray-600">{contact.email} {contact.phone && `\u2022 ${contact.phone}`}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatDate(contact.created_at)}</p>
                  {expandedContact === contact.id && (
                    <div className="mt-4 p-4 bg-gray-50"><p className="text-gray-700 whitespace-pre-wrap">{contact.message}</p></div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setExpandedContact(expandedContact === contact.id ? null : contact.id)} className="p-2 text-gray-400 hover:text-brand-dark" title="View message">
                    {expandedContact === contact.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {contact.status === 'new' && <button onClick={() => updateStatus(contact.id, 'read')} className="p-2 text-gray-400 hover:text-blue-600" title="Mark as read"><Eye className="w-4 h-4" /></button>}
                  {contact.status !== 'responded' && <button onClick={() => updateStatus(contact.id, 'responded')} className="p-2 text-gray-400 hover:text-green-600" title="Mark as responded"><Check className="w-4 h-4" /></button>}
                  {user?.role === 'admin' && <button onClick={() => deleteContact(contact.id)} className="p-2 text-gray-400 hover:text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactsTab;
